import React from "react";
import { Leaf, ShieldCheck, HandHeart, Sparkles, Heart, Award } from "lucide-react";
import { BUSINESS } from "@/data/business";
import { BookButton } from "@/components/site/BookButton";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";

const VALUES = [
  { icon: Leaf, title: "Organic & Natural", desc: "We use only the highest-quality, natural products for gentle, effective results." },
  { icon: Heart, title: "Personalized", desc: "Every treatment is carefully tailored to your skin's unique needs and goals." },
  { icon: Award, title: "Precision & Expertise", desc: "Over a decade of licensed, professional experience you can trust." },
];

const IntroSection = () => (
  <section className="relative overflow-hidden">
    <div className="hero-accent absolute inset-0" aria-hidden="true" />
    <div className="container-x relative grid grid-cols-1 items-center gap-10 py-14 sm:py-20 lg:grid-cols-2 lg:gap-16">
      <Reveal>
        <div className="glass-strong overflow-hidden p-2 sm:p-3">
          <img
            src="/images/about-me.webp"
            alt="Bahra, licensed skincare specialist and owner of Bahra Skincare"
            className="h-[440px] w-full rounded-[14px] object-cover object-top sm:h-[560px]"
          />
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <span className="eyebrow">About Me</span>
        <h1 className="mt-4 text-4xl font-light leading-tight tracking-tight sm:text-5xl">
          Your partner on the journey to radiant skin
        </h1>
        <p className="mt-6 text-base leading-relaxed text-[color:var(--text-muted)]">{BUSINESS.about.bio}</p>
        <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-sm text-[color:var(--text-muted)]">
          <span className="inline-flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.75} /> Licensed Specialist</span>
          <span className="inline-flex items-center gap-2"><Sparkles size={16} strokeWidth={1.75} /> 10+ Years Experience</span>
          <span className="inline-flex items-center gap-2"><HandHeart size={16} strokeWidth={1.75} /> Women-Owned</span>
        </div>
        <div className="mt-8">
          <BookButton testId="about-book-appointment-button" />
        </div>
      </Reveal>
    </div>
  </section>
);

const PhilosophySection = () => (
  <section className="section-pad bg-[color:rgba(217,183,122,0.13)]">
    <div className="container-x grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
      <Reveal>
        <p className="eyebrow">Our Philosophy</p>
        <h2 className="mt-3 text-3xl font-light tracking-tight sm:text-4xl">The youthful, radiant glow you deserve</h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="text-base leading-relaxed text-[color:var(--text-muted)]">{BUSINESS.about.description}</p>
        <p className="mt-4 text-base leading-relaxed text-[color:var(--text-muted)]">
          Whether you are seeking routine beauty maintenance or a relaxing day of rejuvenation, we have you covered.
        </p>
      </Reveal>
    </div>
  </section>
);

const ValuesSection = () => (
  <section className="section-pad">
    <div className="container-x">
      <Reveal className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">What Guides Us</p>
        <h2 className="mt-3 text-3xl font-light tracking-tight sm:text-4xl">Values woven into every visit</h2>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.06}>
            <div className="glass glass-hover h-full p-7 text-center">
              <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-[14px] bg-[color:rgba(185,163,143,0.22)]">
                <v.icon size={22} strokeWidth={1.6} className="text-[color:var(--text)]" />
              </span>
              <h3 className="mt-5 text-lg font-medium">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-muted)]">{v.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Bahra | Licensed Skincare Specialist in Niles, IL | Bahra Skincare"
        description="Meet the licensed skincare specialist behind Bahra Skincare. Over 10 years of experience helping clients achieve healthy, radiant skin with personalized, organic and natural care in Niles, IL."
        path="/about"
      />
      <IntroSection />
      <PhilosophySection />
      <ValuesSection />
    </div>
  );
}
